/**
 * Tipos do módulo fiscal / NFS-e (Fase 0). Derivados dos tipos gerados do
 * Supabase (integrations/supabase/types) para ficarem sempre em sincronia com o
 * schema, mais alguns rótulos de UI.
 */
import type { Database } from '@/integrations/supabase/types';

type DB = Database['public'];

export type TaxpayerType = DB['Enums']['taxpayer_type'];
export type FiscalProvider = DB['Enums']['fiscal_provider'];
export type FiscalAmbiente = DB['Enums']['fiscal_ambiente'];

export type FiscalProfile = DB['Tables']['fiscal_profiles']['Row'];
export type FiscalProfileInsert = DB['Tables']['fiscal_profiles']['Insert'];
export type FiscalProfileUpdate = DB['Tables']['fiscal_profiles']['Update'];
export type Municipality = DB['Tables']['municipalities']['Row'];
export type FiscalCertificate = DB['Tables']['fiscal_certificates']['Row'];

export type NfseDocument = DB['Tables']['nfse_documents']['Row'];
export type NfseDocumentInsert = DB['Tables']['nfse_documents']['Insert'];
export type NfseEvent = DB['Tables']['nfse_events']['Row'];
export type NfseStatus = DB['Enums']['nfse_status'];
export type NfseEventType = DB['Enums']['nfse_event_type'];

/**
 * Rótulo pt-BR + tom por tipo de evento da timeline (nfse_events). O tipo
 * Record<NfseEventType, …> é EXAUSTIVO: se o schema ganhar um novo tipo de
 * evento, o `tsc` quebra aqui até alguém dar um rótulo — a auditoria nunca mostra
 * um `type` cru sem tradução.
 */
export const NFSE_EVENT_META: Record<NfseEventType, { label: string; tone: 'neutral' | 'progress' | 'success' | 'error' }> = {
  created: { label: 'Criada', tone: 'neutral' },
  ready: { label: 'Pronta', tone: 'neutral' },
  queued: { label: 'Na fila', tone: 'progress' },
  submitting: { label: 'Enviando ao provedor', tone: 'progress' },
  transmitted: { label: 'Transmitida', tone: 'progress' },
  authorized: { label: 'Autorizada', tone: 'success' },
  rejected: { label: 'Rejeitada', tone: 'error' },
  reconciliation_required: { label: 'Reconciliação necessária', tone: 'error' },
  reconciled: { label: 'Reconciliada', tone: 'success' },
  retried: { label: 'Retentativa', tone: 'progress' },
  cancel_requested: { label: 'Cancelamento solicitado', tone: 'progress' },
  cancel_processing: { label: 'Cancelando', tone: 'progress' },
  cancel_denied: { label: 'Cancelamento negado', tone: 'error' },
  cancelled: { label: 'Cancelada', tone: 'neutral' },
  danfse_generated: { label: 'DANFSE gerada', tone: 'neutral' },
  error: { label: 'Erro', tone: 'error' },
};

/** Rótulos e cor de badge por status da nota (máquina de estados §10). */
export const NFSE_STATUS_META: Record<NfseStatus, { label: string; tone: 'neutral' | 'progress' | 'success' | 'error' }> = {
  draft: { label: 'Rascunho', tone: 'neutral' },
  ready: { label: 'Pronta', tone: 'neutral' },
  queued: { label: 'Na fila', tone: 'progress' },
  submitting: { label: 'Enviando', tone: 'progress' },
  processing_provider: { label: 'Processando', tone: 'progress' },
  authorized: { label: 'Autorizada', tone: 'success' },
  rejected: { label: 'Rejeitada', tone: 'error' },
  unknown_reconciliation_required: { label: 'Reconciliar', tone: 'error' },
  cancellation_queued: { label: 'Cancelamento na fila', tone: 'progress' },
  cancellation_processing: { label: 'Cancelando', tone: 'progress' },
  cancelled: { label: 'Cancelada', tone: 'neutral' },
  cancellation_denied: { label: 'Cancelamento negado', tone: 'error' },
};

/** Estados da máquina de onboarding fiscal. */
export const FISCAL_ONBOARDING = {
  IDENTITY: 100,
  MUNICIPAL: 101,
  CERTIFICATE: 102,
  DONE: 200,
} as const;

export const TAXPAYER_LABELS: Record<TaxpayerType, string> = {
  autonomo_informal: 'Autônomo (sem CNPJ)',
  mei: 'MEI',
  me_epp: 'ME / EPP',
};

export const TAXPAYER_DESCRIPTIONS: Record<TaxpayerType, string> = {
  autonomo_informal:
    'Trabalho por conta própria, sem CNPJ. Não emito NFS-e — uso Recibo.',
  mei: 'Microempreendedor Individual. Emito NFS-e pelo Padrão Nacional.',
  me_epp: 'Microempresa ou Empresa de Pequeno Porte, com CNPJ.',
};
